// What every panel and dock part may reach without props (slice 05): the
// table it is at, the window it is in, and the shell's two verbs — add
// something to a slot, take a tool off a bar.
import { createContext, useContext, type ReactNode } from 'react';
import type { DeviceClass, SlotId, TableView, UserSettings, ViewId } from '@dragons-table/vtt-shared';
import type { TableResult } from '../table';
import type { DockSlots, ToolId } from './dockHandle';
import type { Menu } from './TopBar';

export interface ShellContextValue {
  view: ViewId;
  campaignId: string;
  table: TableView;
  /** A table call's outcome, applied by the page (the view refreshed, a refusal said). */
  onResult: (r: TableResult) => void;
  /** The board, rendered in the main window's centre. */
  board: ReactNode;
  /** Open the "add to this bar" chooser for a slot: the `+` on every bar. */
  openAdd: (slot: SlotId) => void;
  /** Take a tool off its bar (demotion — it stays in the menus). */
  demote: (tool: ToolId) => void;
  /** The person's account-wide settings (the delete confirmation among them), and the way to change them. */
  settings: UserSettings;
  onSettings: (settings: UserSettings) => void;
  /** The class of screen (issue 1179): which stored layout this is, and which stock it starts from. Absent is desktop. */
  device?: DeviceClass;
  /** The phone layout (issue 1242): the board alone, the side bars opened on demand. Absent is not. */
  phone?: boolean;
  /** The stock arrangement for this window, screen and role (issue 1179); absent is the desktop's. */
  stock?: DockSlots;
  /** Open the DM's Stat block sections setting (issue 1575): the ⚙ on the Properties bar. Absent for anyone who has none. */
  openStatBlockSettings?: () => void;
  /** The icons the person picked for tools' tabs (qa-runs#404), by tool id; a tool not named shows its name. */
  icons?: Record<string, string>;
  /** The names the person gave tools' tabs (qa-runs#1431), by tool id; shown on the tab only. */
  names?: Record<string, string>;
  /** Open a tab's menu (qa-runs#404): move it left, right or to the other bar, pick its icon, close it. */
  openTabMenu?: (tool: ToolId) => void;
}

export const ShellContext = createContext<ShellContextValue | null>(null);

export function useShell(): ShellContextValue {
  const v = useContext(ShellContext);
  if (!v) throw new Error('useShell outside the shell');
  return v;
}

/**
 * The top bar's menus as the shell built them (issue 1541), for the board's tool column: each of its shortcuts runs the
 * menu item's own handler and reads its tick, so the column never does what a menu does not. Its own context, so a
 * menu changing (a selection, a tool turned on) re-renders the column, not every panel. Empty outside the shell.
 */
export const ShellMenusContext = createContext<Menu[]>([]);

export function useShellMenus(): Menu[] {
  return useContext(ShellMenusContext);
}
